/** Header-action builder for the Projects list view.
 *  Owns: Search, Filters (with active-filter badge), and New Project.
 *  All actions fire window events that ProjectListView listens for. No JSX. */

import { Search, Filter, Plus, ChevronsDownUp, ChevronsUpDown, Table, KanbanSquare, Layers } from '@/shared/ui/icons';
import type { MobileHeaderAction } from './types';

export interface ProjectsHeaderState {
  activeFilterCount: number;
  projectCount: number;
  /** True when every visible stage group is collapsed — flips the
   *  collapse-all icon to "expand all". */
  allCollapsed: boolean;
  /** Internal-team only (super_admin / internal). Gates the New Project pill. */
  canCreate?: boolean;
  canCreateEnquiry?: boolean;
  presentation?: 'table' | 'kanban';
  canSwitchPresentation?: boolean;
  kanbanGrouping?: 'stage' | 'pipeline';
}

export interface ProjectsHeaderCtx {
  projectsHeaderState: ProjectsHeaderState | null;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
}

/** Appends Projects-list pills to the caller's leading/trailing action arrays.
 *  Called only when `currentView === 'projects'`. */
export function buildProjectsHeaderActions(ctx: ProjectsHeaderCtx): void {
  const { projectsHeaderState, leadingActions, trailingActions, pushNotificationsAction } = ctx;

  leadingActions.push({
    id: 'projects-search',
    icon: Search,
    ariaLabel: 'Search projects',
    onClick: () => window.dispatchEvent(new CustomEvent('projects:toggle-search')),
  });
  // Filter goes trailing. Stated carefully, because this comment used to claim
  // "per Apple HIG" and that overstates it: the HIG assigns filter and sort to
  // no edge at all. What it DOES state is that the leading edge is a closed
  // enumeration — back/dismiss, sidebar toggle, title, document menu — and
  // that leading items are non-customizable so they stay always available.
  // Filter isn't on that list, so trailing (or centre) is where it can go.
  trailingActions.push({
    id: 'projects-filters',
    icon: Filter,
    ariaLabel: 'Filters',
    onClick: () => window.dispatchEvent(new CustomEvent('projects:open-filters')),
    badge: projectsHeaderState?.activeFilterCount ?? 0,
    // 'view' not 'page': filter and collapse-all both configure how the list is
    // shown, so they belong in one capsule. Left as 'page' they were separated
    // by collapse-all's 'view', giving page|view|page — three clusters for two
    // controls that do the same kind of job.
    group: 'view',
  });
  if (projectsHeaderState?.canSwitchPresentation) {
    const kanban = projectsHeaderState.presentation === 'kanban';
    trailingActions.push({
      id: 'projects-presentation', icon: kanban ? KanbanSquare : Table,
      ariaLabel: `Project view: ${kanban ? 'Kanban' : 'Table'}`, group: 'view',
      subActions: [
        { id: 'table', label: 'Table', icon: Table, active: !kanban,
          onClick: () => window.dispatchEvent(new CustomEvent('projects:set-presentation', { detail: { view: 'table' } })) },
        { id: 'kanban', label: 'Kanban', icon: KanbanSquare, active: kanban,
          onClick: () => window.dispatchEvent(new CustomEvent('projects:set-presentation', { detail: { view: 'kanban' } })) },
      ],
    });
    if (kanban) {
      const byStatus = projectsHeaderState.kanbanGrouping !== 'stage';
      trailingActions.push({
        id: 'projects-grouping', icon: Layers,
        ariaLabel: `Group projects by: ${byStatus ? 'Pipeline status' : 'Delivery stage'}`, group: 'view',
        subActions: [
          { id: 'pipeline', label: 'Pipeline status', active: byStatus, onClick: () => window.dispatchEvent(new CustomEvent('projects:set-grouping', { detail: { grouping: 'pipeline' } })) },
          { id: 'stage', label: 'Delivery stage', active: !byStatus, onClick: () => window.dispatchEvent(new CustomEvent('projects:set-grouping', { detail: { grouping: 'stage' } })) },
        ],
      });
    }
  }
  if (projectsHeaderState?.presentation !== 'kanban') {
  const allCollapsed = projectsHeaderState?.allCollapsed ?? false;
  // View configuration, not navigation, so trailing — same reasoning as the
  // filter above. Apple's leading edge is a closed list (back/dismiss,
  // sidebar toggle, title, document menu) and nothing else qualifies.
  trailingActions.push({
    id: 'projects-collapse-all',
    icon: allCollapsed ? ChevronsUpDown : ChevronsDownUp,
    ariaLabel: allCollapsed ? 'Expand all stages' : 'Collapse all stages',
    onClick: () => window.dispatchEvent(new CustomEvent('projects:toggle-collapse-all')),
    group: 'view',
  });
  }
  // New Project is internal-team only — hidden entirely for members/clients,
  // mirroring onAddProject (see AuthenticatedWorkspace).
  if (projectsHeaderState?.canCreate || projectsHeaderState?.canCreateEnquiry) {
    trailingActions.push({
      id: 'projects-add',
      icon: Plus,
      ariaLabel: projectsHeaderState.canCreateEnquiry ? 'Create' : 'New project',
      desktopLabel: projectsHeaderState.canCreateEnquiry ? 'Create' : 'New project',
      ...(projectsHeaderState.canCreateEnquiry ? { subActions: [
        ...(projectsHeaderState.canCreate ? [{ id: 'project', label: 'New project', onClick: () => window.dispatchEvent(new CustomEvent('projects:create-new')) }] : []),
        { id: 'enquiry', label: 'New enquiry', onClick: () => window.dispatchEvent(new CustomEvent('projects:create-enquiry')) },
      ] } : { onClick: () => window.dispatchEvent(new CustomEvent('projects:create-new')) }),
      group: 'page',
    });
  }
  pushNotificationsAction();
}
